export const GTM_CONTAINER_ID = import.meta.env?.VITE_GTM_ID || "GTM-P5T86LDS";
// GTM owns this Google tag. Never load gtag.js/config separately here.
export const GA4_MEASUREMENT_ID = "G-50YDGP1MVR";
export const CONSENT_KEY = "kate:marketing-consent:v1";
export const CONSENT_VERSION = 1;
export const CONSENT_MAX_AGE = 180 * 24 * 60 * 60 * 1000;
export const OPEN_CONSENT_EVENT = "kate:open-cookie-preferences";
const RESET_PARAM = "kate-consent-reset";
const productionHosts = new Set(["trykate.app", "www.trykate.app"]);

export type ConsentChoice = { version: number; analytics: boolean; marketing: boolean; savedAt: number };
export type PageContext = { path: string; title: string; language: string };
type TrackingWindow = Window & { dataLayer?: unknown[]; gtag?: (...args: unknown[]) => void };
type TrackingOptions = { enabled: boolean; granularConsentVerified?: boolean; containerId?: string; now?: () => number };

export function parseConsent(raw: string | null, now = Date.now()): ConsentChoice | null {
  try {
    if (!raw || raw.length > 1000) return null;
    const value = JSON.parse(raw);
    if (value?.version !== CONSENT_VERSION || typeof value.analytics !== "boolean" || typeof value.marketing !== "boolean" || !Number.isSafeInteger(value.savedAt) || value.savedAt > now || now - value.savedAt >= CONSENT_MAX_AGE) return null;
    return { version: CONSENT_VERSION, analytics: value.analytics, marketing: value.marketing, savedAt: value.savedAt };
  } catch { return null; }
}

export function permitsContainer(choice: ConsentChoice | null, granularConsentVerified = false) {
  // Unverified third-party tags do not necessarily honour Google Consent Mode.
  return Boolean(choice && (granularConsentVerified ? choice.analytics || choice.marketing : choice.analytics && choice.marketing));
}

export function createMarketingTracking(win: TrackingWindow, doc: Document, options: TrackingOptions) {
  const now = options.now || Date.now;
  const containerId = options.containerId || GTM_CONTAINER_ID;
  const enabled = options.enabled && productionHosts.has(win.location.hostname) && /^GTM-[A-Z0-9]+$/.test(containerId);
  let initialized = false;
  let loaded = false;
  let paused = false;
  let choice: ConsentChoice | null = null;
  let page: PageContext | undefined;
  let lastPage = "";
  const listeners = new Set<() => void>();
  const queue = (...args: unknown[]) => win.gtag?.(...args);
  const notify = () => listeners.forEach(listener => listener());
  const consentValues = (current: ConsentChoice | null) => ({ analytics_storage: current?.analytics ? "granted" : "denied", ad_storage: current?.marketing ? "granted" : "denied", ad_user_data: current?.marketing ? "granted" : "denied", ad_personalization: current?.marketing ? "granted" : "denied", functionality_storage: "denied", personalization_storage: "denied", security_storage: "granted" });
  const read = () => { try { const raw = win.localStorage.getItem(CONSENT_KEY); const parsed = parseConsent(raw, now()); if (raw && !parsed) { try { win.localStorage.removeItem(CONSENT_KEY); } catch { /* Still deny if cleanup is blocked. */ } } return parsed; } catch { return null; } };
  const persist = (current: ConsentChoice) => { try { win.localStorage.setItem(CONSENT_KEY, JSON.stringify(current)); } catch { try { win.localStorage.removeItem(CONSENT_KEY); } catch { /* Memory-only consent; no storage access is assumed. */ } } };
  const clearTrackingCookies = () => {
    for (const item of doc.cookie.split(";")) {
      const name = item.trim().split("=")[0] || "";
      if (!/^_(?:ga(?:_|$)|gid$|gat(?:_|$)|gcl_|fbp$|fbc$)/.test(name)) continue;
      for (const domain of ["", `; Domain=${win.location.hostname}`, "; Domain=trykate.app"]) doc.cookie = `${name}=; Max-Age=0; Path=/; SameSite=Lax${domain}`;
    }
  };
  const stopAndReload = () => {
    paused = true;
    try { clearTrackingCookies(); } catch { /* Cookie access must not prevent withdrawal. */ }
    // A loaded third-party script cannot be reliably unloaded. Full navigation
    // stops it; this marker also fails closed if preference storage is blocked.
    const destination = new URL(win.location.href);
    destination.searchParams.set(RESET_PARAM, "1");
    win.location.replace(destination.href);
  };
  const pushPage = () => {
    if (!enabled || !loaded || paused || !permitsContainer(choice, options.granularConsentVerified) || !page || page.path === lastPage) return;
    lastPage = page.path;
    win.dataLayer?.push({ event: "kate_page_view", page_path: page.path, page_location: `${win.location.origin}${page.path}`, page_title: page.title, page_language: page.language, kate_event_id: win.crypto?.randomUUID?.() || `${now().toString(36)}-${Math.random().toString(36).slice(2)}` });
  };
  const loadContainer = () => {
    if (!enabled || loaded || paused || !permitsContainer(choice, options.granularConsentVerified)) return;
    loaded = true;
    win.dataLayer?.push({ "gtm.start": now(), event: "gtm.js" });
    const script = doc.createElement("script");
    script.id = "kate-google-tag-manager";
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(containerId)}`;
    script.referrerPolicy = "strict-origin-when-cross-origin";
    script.onerror = () => { loaded = false; lastPage = ""; script.remove(); notify(); };
    doc.head.appendChild(script);
    pushPage();
  };
  const apply = (next: ConsentChoice | null) => {
    const withdrawing = loaded && Boolean(choice && ((!next?.analytics && choice.analytics) || (!next?.marketing && choice.marketing)));
    choice = next;
    queue("consent", "update", consentValues(choice));
    win.dataLayer?.push({ ...(withdrawing ? {} : { event: "kate_consent_updated" }), kate_consent: { analytics: Boolean(choice?.analytics), marketing: Boolean(choice?.marketing) } });
    notify();
    if (withdrawing) stopAndReload();
    else loadContainer();
  };
  const initialize = () => {
    if (initialized) return;
    initialized = true;
    win.dataLayer = win.dataLayer || [];
    win.gtag = function(..._args: unknown[]) { win.dataLayer!.push(arguments); };
    queue("consent", "default", consentValues(null));
    queue("set", "ads_data_redaction", true);
    queue("set", "url_passthrough", false);
    const address = new URL(win.location.href);
    const reset = address.searchParams.has(RESET_PARAM);
    if (reset) { address.searchParams.delete(RESET_PARAM); win.history.replaceState(win.history.state, "", address.href); }
    let stored = read();
    if (reset && permitsContainer(stored, options.granularConsentVerified)) { stored = null; try { win.localStorage.removeItem(CONSENT_KEY); } catch { /* Deny in memory. */ } }
    apply(stored);
    win.addEventListener("storage", event => { if (event.key === CONSENT_KEY || event.key === null) apply(read()); });
  };
  return {
    initialize,
    getState: () => ({ choice, loaded, enabled, granularConsentVerified: Boolean(options.granularConsentVerified) }),
    subscribe: (listener: () => void) => { listeners.add(listener); return () => { listeners.delete(listener); }; },
    save: (analytics: boolean, marketing: boolean) => { initialize(); const next = { version: CONSENT_VERSION, analytics, marketing, savedAt: now() }; persist(next); apply(next); },
    page: (context: PageContext) => {
      initialize();
      if (choice && !parseConsent(JSON.stringify(choice), now())) { try { win.localStorage.removeItem(CONSENT_KEY); } catch { /* Deny in memory. */ } apply(null); }
      // Never send URL queries/fragments or form/prompt/account values.
      page = { ...context, path: context.path.split(/[?#]/)[0] || "/" };
      pushPage();
    }
  };
}

let tracker: ReturnType<typeof createMarketingTracking> | undefined;
export function marketingTracking() {
  if (typeof window === "undefined" || typeof document === "undefined") return undefined;
  return tracker ||= createMarketingTracking(window, document, { enabled: import.meta.env?.PROD === true, granularConsentVerified: import.meta.env?.VITE_GTM_GRANULAR_CONSENT_VERIFIED === "true" });
}

export function openCookiePreferences() { window.dispatchEvent(new Event(OPEN_CONSENT_EVENT)); }
